// atividades.js - Serviço central do Modo Atividades (Fase A)
//
// Fonte dos conteúdos: o BACKEND (mundos e palavras cadastrados no Strapi —
// dá para adicionar novos temas sem mexer no site). Se o backend ainda não
// tiver as rotas publicadas (ou estiver fora do ar), usamos a cópia local
// embutida no site (assets/atividades-seed.json) — a criança nunca fica sem jogar.
//
// Progresso (estrelas): guardado no backend por perfil + espelho no navegador.

import { GET, POST } from './api';
import seedLocal from 'assets/atividades-seed.json';
import missoesSeed from 'assets/missoes-seed.json';

export const PALAVRAS_POR_FASE = 4;

const urlImagem = (slug, ingles) => `/atividades/img/${slug}/${ingles}.png`;
const urlAudio = (slug, ingles) => `/atividades/audio/${slug}/${ingles}.mp3`;
// Áudio em espanhol: nome do arquivo pelo inglês (estável/ASCII), fala em espanhol.
const urlAudioEs = (slug, ingles) => `/atividades/audio_es/${slug}/${ingles}.mp3`;

// ---------- Idioma das Atividades (Inglês / Espanhol) ----------
const chaveIdiomaAtiv = (dono) => `jk_ativ_idioma_${dono || 'guest'}`;
export const getIdiomaAtiv = (dono) => localStorage.getItem(chaveIdiomaAtiv(dono)) || '';
export const setIdiomaAtiv = (dono, idioma) => localStorage.setItem(chaveIdiomaAtiv(dono), idioma);
// Interpreta a string do idioma (inclusive a que o chat guarda) → 'ingles' | 'espanhol' | ''
export const normalizarIdioma = (v) => {
  const s = (v || '').toString().toLowerCase();
  if (s.includes('esp') || s.includes('span') || s === 'es') return 'espanhol';
  if (s.includes('ing') || s.includes('engl') || s === 'en') return 'ingles';
  return '';
};
// Aplica o idioma às palavras: define 'alvo' (palavra exibida) e o 'audio_url' certo,
// mantendo 'ingles' como chave estável interna dos jogos.
export const aplicarIdioma = (palavras, idioma) => (palavras || []).map((p) => ({
  ...p,
  alvo: idioma === 'espanhol' ? (p.espanhol || p.ingles) : p.ingles,
  audio_url: idioma === 'espanhol'
    ? urlAudioEs(p.mundo_slug, p.ingles)
    : (p.audio_url || urlAudio(p.mundo_slug, p.ingles)),
}));

// Reserva do espanhol pela cópia local (caso o backend ainda não tenha preenchido).
const espanholDoSeed = (slug, ingles) => {
  const m = seedLocal.mundos.find((x) => x.slug === slug);
  const p = m && m.palavras.find((w) => w.ingles === ingles);
  return p ? (p.espanhol || '') : '';
};

// ---------- Conteúdo (mundos e palavras) ----------

// Cada mundo tem um "selo" (imagem) publicado no site, junto do slug.
const capaMundo = (slug) => `/atividades/capas/${slug}.png`;

export const getMundos = async () => {
  try {
    const r = await GET('/atividade-mundos', true);
    if (r && Array.isArray(r.data) && r.data.length > 0) {
      // Selo do mundo: usa a imagem subida no painel (MediaLibrary/netstorage) se houver;
      // senão, cai no selo local publicado junto do site.
      return r.data.map((m) => ({ ...m, capa_url: m.imagem_url || capaMundo(m.slug) }));
    }
  } catch (e) {
    // segue para a cópia local
  }
  return seedLocal.mundos.map((m) => ({
    slug: m.slug,
    nome: m.nome,
    emoji: m.emoji,
    personagem: m.personagem,
    cor: m.cor,
    ordem: m.ordem,
    capa_url: capaMundo(m.slug),
  }));
};

export const getPalavras = async (mundoSlug) => {
  let base = null;
  try {
    const r = await GET(`/atividade-palavras?mundo=${encodeURIComponent(mundoSlug)}`, true);
    if (r && Array.isArray(r.data) && r.data.length > 0) base = r.data;
  } catch (e) {
    // segue para a cópia local
  }
  if (!base) {
    const mundo = seedLocal.mundos.find((m) => m.slug === mundoSlug);
    base = (mundo ? mundo.palavras : []).map((p) => ({
      mundo_slug: mundoSlug,
      ingles: p.ingles,
      espanhol: p.espanhol || '',
      portugues: p.portugues,
      ordem: p.ordem,
      imagem_url: urlImagem(mundoSlug, p.ingles),
      audio_url: urlAudio(mundoSlug, p.ingles),
    }));
  }
  // Garante o espanhol mesmo se o backend ainda não tiver preenchido (usa a cópia local).
  return base.map((p) => ({ ...p, espanhol: p.espanhol || espanholDoSeed(mundoSlug, p.ingles) }));
};

// ---------- Progresso (estrelas por fase) ----------
// Formato local: { "animais:1": 3, "animais:2": 1, "cores:1": 2, ... }

const chaveLocal = (dono) => `jk_atividades_${dono || 'guest'}`;

export const getProgressoLocal = (dono) => {
  try {
    const p = JSON.parse(localStorage.getItem(chaveLocal(dono)) || '{}');
    return p && typeof p === 'object' ? p : {};
  } catch (e) {
    return {};
  }
};

const setProgressoLocal = (dono, progresso) => {
  localStorage.setItem(chaveLocal(dono), JSON.stringify(progresso));
};

// Busca o progresso no backend e funde com o local (fica sempre o melhor)
export const carregarProgresso = async (profileId, dono) => {
  const local = getProgressoLocal(dono);
  if (profileId) {
    try {
      const r = await GET(`/atividade-progressos?profile=${profileId}`, true);
      if (r && Array.isArray(r.data)) {
        for (const linha of r.data) {
          const k = `${linha.mundo_slug}:${linha.fase}`;
          local[k] = Math.max(local[k] || 0, linha.estrelas || 0);
        }
        setProgressoLocal(dono, local);
      }
    } catch (e) {
      // backend indisponível: segue com o local
    }
  }
  return local;
};

// Salva o resultado de uma fase (local na hora + backend em segundo plano)
export const salvarEstrelas = (profileId, dono, mundoSlug, fase, estrelas) => {
  const local = getProgressoLocal(dono);
  const k = `${mundoSlug}:${fase}`;
  local[k] = Math.max(local[k] || 0, estrelas);
  setProgressoLocal(dono, local);

  if (profileId) {
    POST(
      `/atividade-progressos?profile=${profileId}`,
      { data: { mundo_slug: mundoSlug, fase, estrelas } },
      true
    ).catch(() => {
      // backend indisponível: o espelho local já guardou
    });
  }
  return local;
};

// ---------- Áudio de pronúncia ----------

export const tocarAudio = (url) => {
  try {
    const audio = new Audio(url);
    audio.play().catch(() => {});
  } catch (e) {
    // navegador sem suporte: silencioso
  }
};

// Voz-guia dos mascotes: frases curtas gravadas 1 vez (custo zero por uso),
// como fazem Khan Academy Kids e Lingokids — essencial p/ quem ainda não lê.
export const tocarVoz = (personagem, chave) => {
  tocarAudio(`/atividades/vozes/${personagem || 'lulu'}/${chave}.mp3`);
};

// ---------- Meta diária (padrão Duolingo: alvo pequeno e visível) ----------
export const META_FASES_DIA = 1;
const chaveMeta = (dono) => `jk_meta_${dono || 'guest'}`;
const diaHoje = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export const getMetaHoje = (dono) => {
  try {
    const m = JSON.parse(localStorage.getItem(chaveMeta(dono)) || 'null');
    if (m && m.dia === diaHoje()) return m.fases || 0;
  } catch (e) { /* segue */ }
  return 0;
};
export const registrarFaseNaMeta = (dono) => {
  const fases = getMetaHoje(dono) + 1;
  try { localStorage.setItem(chaveMeta(dono), JSON.stringify({ dia: diaHoje(), fases })); } catch (e) { /* segue */ }
  return fases;
};

/* ==================== GAMIFICAÇÃO (Fase B) ==================== */

// Catálogo de 30 figurinhas colecionáveis (imagens em public/atividades/figurinhas)
// origem: 'marco' = ganha por conquista | 'pacote' = vem nos pacotes | 'rara' = coleção completa
export const CUSTO_PACOTE = 50; // moedas por pacote
export const FIGURINHAS = [
  // --- Conquistas (marcos) ---
  { id: 'lulu_oi', nome: 'Lulu Boas-vindas', origem: 'marco', dica: 'Jogar pela primeira vez' },
  { id: 'nando_joia', nome: 'Nando Joia', origem: 'marco', dica: 'Completar sua primeira fase' },
  { id: 'lulu_estrela', nome: 'Lulu Estrela', origem: 'marco', dica: 'Ganhar 3 estrelas numa fase' },
  { id: 'nando_trofeu', nome: 'Nando Campeão', origem: 'marco', dica: 'Completar um mundo inteiro' },
  { id: 'lulu_medalha', nome: 'Lulu Medalha', origem: 'marco', dica: 'Completar dois mundos' },
  { id: 'nando_foguete', nome: 'Nando Foguete', origem: 'marco', dica: 'Jogar 3 dias seguidos' },
  { id: 'lulu_campea', nome: 'Lulu Vitoriosa', origem: 'marco', dica: 'Jogar 7 dias seguidos' },
  { id: 'lulu_livro', nome: 'Lulu Estudiosa', origem: 'marco', dica: 'Cumprir uma missão de conversa' },
  { id: 'nando_musica', nome: 'Nando Musical', origem: 'marco', dica: 'Juntar 100 moedas' },
  { id: 'nando_estrela', nome: 'Nando Estrelado', origem: 'marco', dica: 'Juntar 300 moedas' },
  // --- Vêm nos pacotes (compre com moedas) ---
  { id: 'lulu_pula', nome: 'Lulu Pulando', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'lulu_coracao', nome: 'Lulu Coração', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'lulu_pincel', nome: 'Lulu Pintora', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'lulu_oculos', nome: 'Lulu Descolada', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'lulu_flor', nome: 'Lulu das Flores', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'lulu_sorvete', nome: 'Lulu Sorveteira', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'nando_super', nome: 'Nando Super-herói', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'nando_skate', nome: 'Nando Skatista', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'nando_violao', nome: 'Nando Músico', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'nando_pipa', nome: 'Nando da Pipa', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'nando_bola', nome: 'Nando Craque', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'nando_lupa', nome: 'Nando Explorador', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'fig_leao', nome: 'Leãozinho', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'fig_gato', nome: 'Gatinho', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'fig_arcoiris', nome: 'Arco-íris', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'fig_estrela', nome: 'Estrelinha', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'fig_coracao', nome: 'Coraçãozinho', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'fig_sol', nome: 'Solzinho', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  { id: 'fig_foguete', nome: 'Foguetinho', origem: 'pacote', dica: 'Abra pacotes de figurinhas' },
  // --- Rara (coleção completa) ---
  { id: 'dupla_festa', nome: 'Festa da Dupla ⭐', origem: 'rara', dica: 'Colecionar todas as outras!' },
];
export const imagemFigurinha = (id) => `/atividades/figurinhas/${id}.png`;

// Missões de conversa (a ponte com o chat — nosso diferencial!).
// Vêm do BACKEND (dá para adicionar novas pelo painel) com reserva local.
// Cada missão tem 'nivel' (explorador/aventureiro/campeao/todos) — a tela
// mostra a missão certa para o nível de cada criança.
const missoesLocais = () => (missoesSeed.missoes || []).map((m) => ({
  id: m.chave, personagem: m.personagem, modo: m.modo, idioma: m.idioma, nivel: m.nivel,
  titulo: m.titulo, moedas: m.moedas, cor: m.cor,
}));

export const getMissoes = async () => {
  try {
    const r = await GET('/missoes', true);
    if (r && Array.isArray(r.data) && r.data.length > 0) return r.data;
  } catch (e) {
    // segue para a cópia local
  }
  return missoesLocais();
};

// Auto-reabastecimento: quando o estoque de um idioma está acabando, avisa o
// backend (que gera um lote NOVO e INATIVO p/ aprovação humana). Dispara no
// máximo 1x por dia por idioma, em segundo plano (não trava nada).
export const pedirReabastecimento = (idioma) => {
  try {
    const d = new Date();
    const hoje = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    const chave = `jk_reab_${idioma}`;
    if (localStorage.getItem(chave) === hoje) return; // já avisou hoje
    localStorage.setItem(chave, hoje);
    POST('/missoes/reabastecer', { idioma }, true).catch(() => {});
  } catch (e) {
    // silencioso — reabastecer é um bônus, nunca pode atrapalhar a criança
  }
};

// Filtra as missões pelo IDIOMA escolhido e pelo NÍVEL da criança em cada
// mascote (idiomas). As bíblicas (idioma/nivel 'todos') aparecem sempre.
// Assim a missão respeita o idioma e "sobe de nível" junto com a criança.
export const missoesDoNivel = (missoes, nivelPorPersonagem, idioma) => {
  const idi = idioma || 'ingles';
  return (missoes || []).filter((m) => {
    if (m.modo !== 'idiomas') return true;                       // bíblicas: sempre
    if (m.idioma && m.idioma !== 'todos' && m.idioma !== idi) return false; // idioma
    const nivel = (nivelPorPersonagem && nivelPorPersonagem[m.personagem]) || 'explorador';
    return m.nivel === nivel;                                    // nível do mascote
  });
};

// ---- Estado do "perfil gamer" (dias, moedas, figurinhas, missões) ----
const PADRAO_GAMER = { dias_seguidos: 0, ultimo_dia: null, protecoes: 1, moedas: 0, figurinhas: [], missoes_feitas: [] };
const chaveGamer = (dono) => `jk_gamer_${dono || 'guest'}`;

const getGamerLocal = (dono) => {
  try {
    const g = JSON.parse(localStorage.getItem(chaveGamer(dono)) || 'null');
    return g && typeof g === 'object' ? { ...PADRAO_GAMER, ...g } : { ...PADRAO_GAMER };
  } catch (e) {
    return { ...PADRAO_GAMER };
  }
};
const setGamerLocal = (dono, estado) => localStorage.setItem(chaveGamer(dono), JSON.stringify(estado));

const persistirGamer = (profileId, dono, estado) => {
  setGamerLocal(dono, estado);
  if (profileId) {
    POST(`/atividade-perfis?profile=${profileId}`, { data: estado }, true).catch(() => {});
  }
};

export const carregarPerfilGamer = async (profileId, dono) => {
  let estado = getGamerLocal(dono);
  if (profileId) {
    try {
      const r = await GET(`/atividade-perfis?profile=${profileId}`, true);
      if (r && typeof r.dias_seguidos !== 'undefined') {
        // funde: fica com o maior de moedas/dias e junta figurinhas/missões
        estado = {
          dias_seguidos: Math.max(estado.dias_seguidos, r.dias_seguidos || 0),
          ultimo_dia: r.ultimo_dia || estado.ultimo_dia,
          protecoes: typeof r.protecoes === 'number' ? r.protecoes : estado.protecoes,
          moedas: Math.max(estado.moedas, r.moedas || 0),
          figurinhas: Array.from(new Set([...(estado.figurinhas || []), ...(r.figurinhas || [])])),
          missoes_feitas: Array.from(new Set([...(estado.missoes_feitas || []), ...(r.missoes_feitas || [])])),
        };
        setGamerLocal(dono, estado);
      }
    } catch (e) {
      // segue com o local
    }
  }
  return estado;
};

// Data local no formato AAAA-MM-DD
const hojeStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const diffDias = (a, b) => {
  if (!a || !b) return 999;
  return Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000);
};

// Marca que a criança jogou HOJE e atualiza a sequência de dias
export const registrarDiaJogado = (profileId, dono, estado) => {
  const hoje = hojeStr();
  const e = { ...estado };
  if (e.ultimo_dia === hoje) return e; // já contou hoje
  const dias = diffDias(e.ultimo_dia, hoje);
  if (dias === 1) {
    e.dias_seguidos = (e.dias_seguidos || 0) + 1;
  } else if (dias === 2 && (e.protecoes || 0) > 0) {
    e.dias_seguidos = (e.dias_seguidos || 0) + 1; // perdoou 1 dia
    e.protecoes = e.protecoes - 1;
  } else {
    e.dias_seguidos = 1; // recomeça
  }
  // ganha uma proteção a cada 5 dias (máx. 2) — sem culpa de perder um dia
  if (e.dias_seguidos % 5 === 0 && (e.protecoes || 0) < 2) e.protecoes = (e.protecoes || 0) + 1;
  e.ultimo_dia = hoje;
  persistirGamer(profileId, dono, e);
  return e;
};

export const ganharMoedas = (profileId, dono, estado, qtd) => {
  const e = { ...estado, moedas: (estado.moedas || 0) + qtd };
  persistirGamer(profileId, dono, e);
  return e;
};

// Monta a lista de álbuns: Álbum 1 (fixo, no código) + álbuns do BACKEND
// (criados pelo painel, com imagens no netstorage). Se o backend não responder,
// fica só o Álbum 1 — a criança nunca fica sem álbum.
export const getAlbuns = async () => {
  const album1 = {
    numero: 1,
    nome: 'Álbum 1 — Lulu e Nando',
    tema: '',
    figurinhas: FIGURINHAS.map((f) => ({ ...f, imagem_url: imagemFigurinha(f.id) })),
  };
  const albuns = [album1];
  try {
    const [rAlb, rFig] = await Promise.all([GET('/figurinha-albuns', true), GET('/figurinhas', true)]);
    const listaFig = (rFig && Array.isArray(rFig.data)) ? rFig.data : [];
    if (rAlb && Array.isArray(rAlb.data)) {
      for (const a of rAlb.data) {
        const figs = listaFig
          .filter((f) => f.album_numero === a.numero && f.imagem_url)
          .map((f) => ({ id: f.chave, nome: f.nome, origem: f.origem || 'pacote', dica: f.dica || 'Abra pacotes de figurinhas', imagem_url: f.imagem_url }));
        if (figs.length > 0) albuns.push({ numero: a.numero, nome: a.nome, tema: a.tema, figurinhas: figs });
      }
    }
  } catch (e) {
    // sem backend de álbuns: fica só o Álbum 1
  }
  return albuns;
};

// Concede uma figurinha (se ainda não tiver). Retorna { estado, nova: bool }.
// Se um álbum for passado e a criança completar todas as NÃO-raras dele,
// ganha automaticamente a figurinha rara daquele álbum.
export const ganharFigurinha = (profileId, dono, estado, id, album) => {
  const atuais = estado.figurinhas || [];
  if (atuais.includes(id)) return { estado, nova: false };
  const e = { ...estado, figurinhas: [...atuais, id] };
  if (album && Array.isArray(album.figurinhas)) {
    const raras = album.figurinhas.filter((f) => f.origem === 'rara').map((f) => f.id);
    const naoRaras = album.figurinhas.filter((f) => f.origem !== 'rara').map((f) => f.id);
    for (const rara of raras) {
      if (!e.figurinhas.includes(rara) && naoRaras.length > 0 && naoRaras.every((x) => e.figurinhas.includes(x))) {
        e.figurinhas = [...e.figurinhas, rara];
      }
    }
  }
  persistirGamer(profileId, dono, e);
  return { estado: e, nova: true };
};

// Abre um pacote: gasta moedas e sorteia uma figurinha "de pacote" que a
// criança ainda não tem. Retorna { estado, ganhou } ou { estado, erro }.
// Abre um pacote do ÁLBUM informado: gasta moedas e sorteia uma figurinha
// "de pacote" desse álbum que a criança ainda não tem.
export const abrirPacote = (album, profileId, dono, estado) => {
  if ((estado.moedas || 0) < CUSTO_PACOTE) return { estado, erro: 'moedas' };
  const tem = estado.figurinhas || [];
  const possiveis = (album.figurinhas || []).filter((f) => f.origem === 'pacote' && !tem.includes(f.id)).map((f) => f.id);
  if (possiveis.length === 0) return { estado, erro: 'completo' };
  const sorteada = possiveis[Math.floor(Math.random() * possiveis.length)];
  const gasto = { ...estado, moedas: estado.moedas - CUSTO_PACOTE };
  const { estado: novo } = ganharFigurinha(profileId, dono, gasto, sorteada, album); // já persiste
  return { estado: novo, ganhou: sorteada };
};

export const marcarMissaoFeita = (profileId, dono, estado, missaoId, moedas) => {
  const feitas = estado.missoes_feitas || [];
  if (feitas.includes(missaoId)) return estado;
  const e = { ...estado, missoes_feitas: [...feitas, missaoId], moedas: (estado.moedas || 0) + (moedas || 0) };
  persistirGamer(profileId, dono, e);
  return e;
};
